import { ChatConfig, AppState } from '../types';

// 存储键名常量
const STORAGE_KEYS = {
  CHAT_CONFIG: 'chatConfig',
  APP_STATE: 'chatAppState',
  SIDEBAR_HIDDEN: 'sidebarHidden',
  THEME: 'theme',
  LANGUAGE: 'language',
  SETTINGS: 'settings'
} as const;

// 默认配置
const createDefaultConfig = (): ChatConfig => {
  return {
    baseUrl: 'https://api.openai.com/v1/chat/completions',
    apiKey: '',
    models: [
      { name: 'GPT-3.5 Turbo', value: 'gpt-3.5-turbo', displayName: 'GPT-3.5 Turbo' },
      { name: 'GPT-4', value: 'gpt-4', displayName: 'GPT-4' },
      { name: 'GPT-4 Turbo', value: 'gpt-4-turbo', displayName: 'GPT-4 Turbo' }
    ],
    currentModel: 'gpt-3.5-turbo'
  };
};

// 迁移旧配置到新格式
const migrateOldConfig = (oldConfig: any): ChatConfig => {
  // 迁移单个URL配置
  if (oldConfig.apiUrl && oldConfig.apiKey && oldConfig.model) {
    return {
      baseUrl: oldConfig.apiUrl,
      apiKey: oldConfig.apiKey,
      models: [
        { name: oldConfig.model, value: oldConfig.model, displayName: oldConfig.model }
      ],
      currentModel: oldConfig.model
    };
  }
  
  // 迁移多URL配置
  if (oldConfig.urls && Array.isArray(oldConfig.urls) && oldConfig.urls.length > 0) {
    const currentUrl = oldConfig.urls.find((url: any) => url.id === oldConfig.currentUrlId) || oldConfig.urls[0];
    return {
      baseUrl: currentUrl.url,
      apiKey: currentUrl.apiKey,
      models: currentUrl.models || [],
      currentModel: oldConfig.currentModel || (currentUrl.models?.[0]?.value || 'gpt-3.5-turbo')
    };
  }
  
  return createDefaultConfig();
};

// 恢复日期对象
const restoreDates = (data: any): any => {
  if (data && typeof data === 'object') {
    if (data.createdAt && typeof data.createdAt === 'string') {
      data.createdAt = new Date(data.createdAt);
    }
    if (data.updatedAt && typeof data.updatedAt === 'string') {
      data.updatedAt = new Date(data.updatedAt);
    }
    if (data.timestamp && typeof data.timestamp === 'string') {
      data.timestamp = new Date(data.timestamp);
    }
    
    // 递归处理嵌套对象
    for (const key in data) {
      if (Array.isArray(data[key])) {
        data[key] = data[key].map((item: any) => restoreDates(item));
      } else if (typeof data[key] === 'object' && data[key] !== null) {
        data[key] = restoreDates(data[key]);
      }
    }
  }
  return data;
};

export class StorageService {
  // 检查 localStorage 是否可用
  private static isLocalStorageAvailable(): boolean {
    try {
      const test = '__localStorage_test__';
      localStorage.setItem(test, test);
      localStorage.removeItem(test);
      return true;
    } catch {
      return false;
    }
  }

  // 安全地获取 localStorage 项
  private static getItem(key: string): string | null {
    if (!this.isLocalStorageAvailable()) {
      console.warn('localStorage is not available');
      return null;
    }
    try {
      return localStorage.getItem(key);
    } catch (error) {
      console.error(`Error getting item ${key}:`, error);
      return null;
    }
  }

  // 安全地设置 localStorage 项
  private static setItem(key: string, value: string): boolean {
    if (!this.isLocalStorageAvailable()) {
      console.warn('localStorage is not available');
      return false;
    }
    try {
      localStorage.setItem(key, value);
      return true;
    } catch (error) {
      console.error(`Error setting item ${key}:`, error);
      return false;
    }
  }

  // 安全地删除 localStorage 项
  private static removeItem(key: string): boolean {
    if (!this.isLocalStorageAvailable()) {
      return false;
    }
    try {
      localStorage.removeItem(key);
      return true;
    } catch (error) {
      console.error(`Error removing item ${key}:`, error);
      return false;
    }
  }

  // 获取聊天配置
  static getChatConfig(): ChatConfig {
    const saved = this.getItem(STORAGE_KEYS.CHAT_CONFIG);
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        // 检查是否是旧格式配置
        if (parsed.apiUrl && parsed.apiKey && parsed.model) {
          return migrateOldConfig(parsed);
        }
        // 确保新格式配置有models数组
        if (parsed && typeof parsed === 'object') {
          return {
            baseUrl: parsed.baseUrl || '',
            apiKey: parsed.apiKey || '',
            models: Array.isArray(parsed.models) ? parsed.models : [],
            currentModel: parsed.currentModel || 'gpt-3.5-turbo'
          };
        }
        return parsed;
      } catch (error) {
        console.error('Error parsing chat config:', error);
      }
    }
    return createDefaultConfig();
  }

  // 保存聊天配置
  static saveChatConfig(config: ChatConfig): boolean {
    return this.setItem(STORAGE_KEYS.CHAT_CONFIG, JSON.stringify(config));
  }

  // 获取应用状态
  static getAppState(): AppState {
    const saved = this.getItem(STORAGE_KEYS.APP_STATE);
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        // 恢复日期对象
        const restored = restoreDates(parsed);
        return restored;
      } catch (error) {
        console.error('Error parsing app state:', error);
      }
    }
    return {
      sessions: [],
      currentSessionId: null,
      isLoading: false,
      error: null
    };
  }

  // 保存应用状态
  static saveAppState(appState: AppState): boolean {
    return this.setItem(STORAGE_KEYS.APP_STATE, JSON.stringify(appState));
  }

  // 获取侧边栏隐藏状态
  static getSidebarHidden(): boolean {
    const saved = this.getItem(STORAGE_KEYS.SIDEBAR_HIDDEN);
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch (error) {
        console.error('Error parsing sidebar hidden state:', error);
      }
    }
    return false;
  }

  // 保存侧边栏隐藏状态
  static saveSidebarHidden(hidden: boolean): boolean {
    return this.setItem(STORAGE_KEYS.SIDEBAR_HIDDEN, JSON.stringify(hidden));
  }

  // 获取主题设置
  static getTheme(): string {
    const saved = this.getItem(STORAGE_KEYS.THEME);
    return saved || 'light';
  }

  // 保存主题设置
  static saveTheme(theme: string): boolean {
    return this.setItem(STORAGE_KEYS.THEME, JSON.stringify(theme));
  }

  // 获取语言设置
  static getLanguage(): string {
    const saved = this.getItem(STORAGE_KEYS.LANGUAGE);
    return saved || 'zh-CN';
  }

  // 保存语言设置
  static saveLanguage(language: string): boolean {
    return this.setItem(STORAGE_KEYS.LANGUAGE, JSON.stringify(language));
  }

  // 获取其他设置
  static getSettings(): Record<string, any> {
    const saved = this.getItem(STORAGE_KEYS.SETTINGS);
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch (error) {
        console.error('Error parsing settings:', error);
      }
    }
    return {};
  }

  // 保存其他设置
  static saveSettings(settings: Record<string, any>): boolean {
    return this.setItem(STORAGE_KEYS.SETTINGS, JSON.stringify(settings));
  }

  // 更新特定设置
  static updateSettings(updates: Record<string, any>): boolean {
    const currentSettings = this.getSettings();
    const newSettings = { ...currentSettings, ...updates };
    return this.saveSettings(newSettings);
  }

  // 清除所有数据
  static clearAll(): void {
    Object.values(STORAGE_KEYS).forEach(key => {
      this.removeItem(key);
    });
  }

  // 导出所有数据
  static exportData(): Record<string, any> {
    const data: Record<string, any> = {};
    Object.entries(STORAGE_KEYS).forEach(([key, storageKey]) => {
      const value = this.getItem(storageKey);
      if (value) {
        try {
          data[key] = JSON.parse(value);
        } catch {
          data[key] = value;
        }
      }
    });
    return data;
  }

  // 导入数据
  static importData(data: Record<string, any>): boolean {
    try {
      Object.entries(data).forEach(([key, value]) => {
        const storageKey = STORAGE_KEYS[key as keyof typeof STORAGE_KEYS];
        if (storageKey) {
          this.setItem(storageKey, JSON.stringify(value));
        }
      });
      return true;
    } catch (error) {
      console.error('Error importing data:', error);
      return false;
    }
  }

  // 获取存储使用情况
  static getStorageUsage(): { used: number; total: number; percentage: number } {
    if (!this.isLocalStorageAvailable()) {
      return { used: 0, total: 0, percentage: 0 };
    }

    let used = 0;
    Object.values(STORAGE_KEYS).forEach(key => {
      const value = localStorage.getItem(key);
      if (value) {
        used += new Blob([value]).size;
      }
    });

    // localStorage 通常限制为 5-10MB，这里使用 5MB 作为估算
    const total = 5 * 1024 * 1024; // 5MB
    const percentage = (used / total) * 100;

    return { used, total, percentage };
  }

  // 清理过期或损坏的数据
  static cleanup(): void {
    Object.values(STORAGE_KEYS).forEach(key => {
      const value = this.getItem(key);
      if (value) {
        try {
          JSON.parse(value);
        } catch {
          // 如果数据损坏，删除它
          this.removeItem(key);
          console.warn(`Removed corrupted data for key: ${key}`);
        }
      }
    });
    
    // 强制重新创建配置以确保新格式
    const currentConfig = this.getItem(STORAGE_KEYS.CHAT_CONFIG);
    if (currentConfig) {
      try {
        const parsed = JSON.parse(currentConfig);
        // 如果是旧格式，清除并重新创建
        if (parsed.urls || (parsed.apiUrl && !parsed.baseUrl)) {
          this.removeItem(STORAGE_KEYS.CHAT_CONFIG);
          console.log('Cleared old configuration format');
        }
      } catch (error) {
        // 如果解析失败，清除配置
        this.removeItem(STORAGE_KEYS.CHAT_CONFIG);
        console.log('Cleared invalid configuration');
      }
    }
  }
} 